import { Link, useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { maskWholeNumber } from '@givebutter/util';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Progress from '@radix-ui/react-progress';
import { fetchImport } from 'api/imports';
import { ImportContactsOveragesWarning } from 'assets//js/pages/account-settings/imports/ImportContactsOveragesWarning';
import { Badge } from 'assets/js/pages/account-settings/imports/Badge';
import { importFormatted } from 'components/imports/importStrategy';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { groupBy, truncate } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ImportError } from 'types/domain';

const REFRESH_INTERVAL = 5000;

const ImportDetails = () => {
    const { apiUrl } = useAppState();
    const { import_id } = useParams<{ import_id: string }>();
    const { accountId } = useCurrentAccount();
    const importId = parseInt(import_id);

    const { data: report } = useSuspenseQuery({
        queryFn: () => fetchImport(accountId, importId),
        queryKey: accountQueryKeys.importById(accountId, importId),
        refetchInterval: data => {
            if (!data.state.data?.status) {
                return false;
            }

            return ['processing', 'pending', 'queued'].includes(data.state.data!.status)
                ? REFRESH_INTERVAL
                : false;
        },
    });

    const showErrors =
        ['succeeded', 'failed'].includes(report.status) &&
        Array.isArray(report.errors) &&
        report.errors.length > 0;

    return (
        <div className={'flex flex-col gap-y-6'}>
            <header className={'flex flex-col gap-y-4'}>
                <Link
                    to={`/accounts/${accountId}/settings/imports`}
                    className={
                        'flex items-center gap-x-2 text-sm font-medium text-gray-600 hover:text-gray-900'
                    }
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    <span>Back to imports</span>
                </Link>

                <h1 className={'text-2xl font-medium'}>Import details</h1>
            </header>

            {report.resource === 'contacts' && <ImportContactsOveragesWarning />}

            <Box>
                <div className={'flex flex-col gap-y-2 border-b border-gray-200 px-6 py-4'}>
                    <div className={'flex items-baseline gap-x-2'}>
                        <h2 className={'text-xl font-semibold'}>
                            {report.file_name ?? `${importFormatted(report.resource)} import`}
                        </h2>
                        <Badge status={report.status} />
                    </div>
                    {report.status === 'processing' && (
                        <div className={'flex items-center gap-x-2 text-sm text-gray-500'}>
                            <Progress.Root
                                value={report.progress}
                                className={'h-2 w-20 rounded-full bg-gray-200'}
                            >
                                <Progress.Indicator
                                    style={{
                                        width: `${report.progress}%`,
                                    }}
                                    className={`h-full rounded-full bg-blue-500`}
                                />
                            </Progress.Root>
                            <p>{report.progress}% complete</p>
                            <p>•</p>
                            <p>{report.estimated_time_remaining_human}</p>
                        </div>
                    )}
                    {report.status === 'failed' && typeof report.errors === 'string' && (
                        <p className={'text-sm text-red-700'}>{report.errors}</p>
                    )}
                </div>

                <div className={'flex justify-between px-6 py-4'}>
                    <div className={'flex flex-col gap-y-1'}>
                        <p className={'text-sm font-medium text-gray-800'}>Import Type</p>
                        <p className={'text-gray-700'}>{importFormatted(report.resource)}</p>
                    </div>

                    {report.object_type === 'campaign' && (
                        <div className={'flex flex-col gap-y-1'}>
                            <p className={'text-sm font-medium text-gray-800'}>Campaign</p>
                            <Link
                                to={`/accounts/${accountId}/campaigns/${report.object_id}/auction/items`}
                                title={report.object_display_name}
                            >
                                {truncate(report.object_display_name, { length: 30 })}
                            </Link>
                        </div>
                    )}

                    <div className={'flex flex-col gap-y-1'}>
                        <p className={'text-sm font-medium text-gray-800'}>Number of Rows</p>
                        <p className={'text-gray-700'}>{maskWholeNumber(report.rows)}</p>
                    </div>

                    <div className={'flex flex-col gap-y-1'}>
                        <p className={'text-sm font-medium text-gray-800'}>Failed Rows</p>
                        <p className={'text-gray-700'}>{maskWholeNumber(report.error_count)}</p>
                    </div>

                    {report.imported_at !== null && (
                        <div className={'flex flex-col gap-y-1'}>
                            <p className={'text-sm font-medium text-gray-800'}>Imported</p>
                            <p className={'text-gray-700'}>
                                {dateFromServer(report.imported_at).toLocaleString({
                                    ...DateTime.DATETIME_MED,
                                    timeZoneName: 'short',
                                })}
                            </p>
                        </div>
                    )}
                </div>
            </Box>

            {showErrors && (
                <>
                    <div className={'flex items-center justify-between'}>
                        <div className={'flex items-baseline gap-x-2'}>
                            <h2 className={'text-xl font-semibold'}>Errors</h2>
                            <div
                                className={`flex items-center gap-x-1 rounded-full bg-red-50 px-2 py-0.5 text-sm font-medium leading-5 text-red-700`}
                            >
                                Needs Review
                            </div>
                        </div>

                        {report.status === 'succeeded' && (
                            <Button
                                variant="none"
                                color="primary"
                                className={'font-semibold'}
                                onClick={() => {
                                    window.open(
                                        `${apiUrl}/dashboard/accounts/${accountId}/imports/${report.id}/download?failed=true`,
                                        '_blank',
                                    );
                                }}
                            >
                                Download errors as file
                            </Button>
                        )}
                    </div>

                    <div>
                        <div className={'grid grid-cols-12 px-4 pb-4'}>
                            <div className={'col-span-4'}>Row</div>
                            <div className={'col-span-8'}>Error</div>
                        </div>

                        <Box className={'rounded-lg'}>
                            {eachGroupedRow(report.errors)(error => (
                                <div
                                    key={error[0].row}
                                    className={
                                        'grid grid-cols-12 border-b border-gray-200 p-4 last:border-none'
                                    }
                                >
                                    <div className={'col-span-4'}>Row {error[0].row}</div>
                                    <div className={'col-span-8'}>
                                        {error.map(row => (
                                            <p key={row.attribute}>{row.errors[0]}</p>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </Box>
                    </div>
                </>
            )}
        </div>
    );
};

const eachGroupedRow = (errors: ImportError[]) => (callback: (group: ImportError[]) => any) => {
    const errorsByRow = groupBy(errors, error => error.row);

    let results = [];

    for (let row in errorsByRow) {
        results.push(callback(errorsByRow[row]));
    }

    return results;
};

export { ImportDetails };
